import React, { useEffect, useState, useCallback } from 'react';
import { vorApi } from '../services/api';
import { toast } from 'react-toastify';

// ── Shared styles ─────────────────────────────────────────────────────────────

const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' };
const labelStyle = { display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '13px', color: '#374151' };
const btnPrimary = { padding: '8px 18px', backgroundColor: '#6B3FA0', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer', fontSize: '13px' };
const btnDanger  = { padding: '6px 14px', backgroundColor: 'white', color: '#DC2626', border: '1px solid #DC2626', borderRadius: '6px', fontWeight: '600', cursor: 'pointer', fontSize: '12px' };
const btnGhost   = { padding: '6px 14px', backgroundColor: 'white', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '6px', fontWeight: '600', cursor: 'pointer', fontSize: '12px' };

const Modal = ({ title, onClose, children }) => (
  <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{ backgroundColor: 'white', borderRadius: '10px', width: '560px', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px', borderBottom: '1px solid #E5D5F5', backgroundColor: '#F3EEFA', borderRadius: '10px 10px 0 0' }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#4A2B70' }}>{title}</h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#9CA3AF', lineHeight: 1 }}>×</button>
      </div>
      <div style={{ padding: '24px' }}>{children}</div>
    </div>
  </div>
);

const FormRow = ({ label, children }) => (
  <div style={{ marginBottom: '16px' }}>
    <label style={labelStyle}>{label}</label>
    {children}
  </div>
);

const Table = ({ cols, rows, renderRow, loading, emptyMsg }) => (
  <div style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #E5D5F5', overflow: 'hidden' }}>
    {loading ? (
      <div style={{ padding: '40px', textAlign: 'center', color: '#9CA3AF' }}>Loading…</div>
    ) : rows.length === 0 ? (
      <div style={{ padding: '40px', textAlign: 'center', color: '#9CA3AF' }}>{emptyMsg || 'No items found.'}</div>
    ) : (
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ backgroundColor: '#F3EEFA', borderBottom: '2px solid #E5D5F5' }}>
            {cols.map(c => (
              <th key={c} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#4A2B70' }}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.id || i} style={{ borderBottom: '1px solid #F3F4F6', backgroundColor: i % 2 === 0 ? 'white' : '#FAFAFA' }}>
              {renderRow(row)}
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

const Td = ({ children, style }) => (
  <td style={{ padding: '10px 14px', fontSize: '13px', color: '#374151', verticalAlign: 'middle', ...style }}>{children}</td>
);

// ── Tab 1: Risk Tolerance Questions ──────────────────────────────────────────

const QuestionsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ question_text: '', points_if_yes: 10 });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { setLoading(true); const r = await vorApi.listQuestions(); setItems(r.data.questions || []); }
    catch { toast.error('Failed to load questions'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm({ question_text: '', points_if_yes: 10 }); setModal('new'); };
  const openEdit = (q) => { setForm({ question_text: q.question_text, points_if_yes: q.points_if_yes }); setModal(q); };

  const handleSave = async () => {
    if (!form.question_text.trim()) { toast.error('Question text is required'); return; }
    try {
      setSaving(true);
      if (modal === 'new') await vorApi.createQuestion(form);
      else await vorApi.updateQuestion(modal.id, form);
      toast.success(modal === 'new' ? 'Question added' : 'Question updated');
      setModal(null); load();
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (q) => {
    if (!window.confirm(`Delete "${q.question_text}"?`)) return;
    try { await vorApi.deleteQuestion(q.id); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
        <button onClick={openNew} style={btnPrimary}>+ Add Question</button>
      </div>
      <Table
        loading={loading}
        cols={['#', 'Question', 'Points', 'Actions']}
        rows={items}
        emptyMsg="No questions defined yet."
        renderRow={q => (<>
          <Td>{q.id}</Td>
          <Td style={{ maxWidth: '480px' }}>{q.question_text}</Td>
          <Td>{q.points_if_yes}</Td>
          <Td><div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => openEdit(q)} style={btnGhost}>Edit</button>
            <button onClick={() => handleDelete(q)} style={btnDanger}>Delete</button>
          </div></Td>
        </>)}
      />

      {modal && (
        <Modal title={modal === 'new' ? 'Add Question' : 'Edit Question'} onClose={() => setModal(null)}>
          <FormRow label="Question Text">
            <textarea value={form.question_text} onChange={e => setForm(f => ({ ...f, question_text: e.target.value }))}
              rows={3} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Does the organization have…" />
          </FormRow>
          <FormRow label="Points if Yes">
            <input type="number" value={form.points_if_yes} min="0"
              onChange={e => setForm(f => ({ ...f, points_if_yes: Number(e.target.value) }))}
              style={{ ...inputStyle, width: '120px' }} />
          </FormRow>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button onClick={() => setModal(null)} style={btnGhost}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ── Tab 2: Risk Catalog ───────────────────────────────────────────────────────

const RISK_CATEGORIES = [
  'Ransomware', 'Data Breach', 'Business Email Compromise', 'Insider Threat',
  'Third Party/Supply Chain', 'Denial of Service', 'Compliance', 'Physical Security', 'Other',
];

const RisksTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ risk_code: '', risk_name: '', risk_category: '', description: '', default_likelihood: 3, default_impact: 3 });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { setLoading(true); const r = await vorApi.listRisks(); setItems(r.data.risks || []); }
    catch { toast.error('Failed to load risks'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm({ risk_code: '', risk_name: '', risk_category: RISK_CATEGORIES[0], description: '', default_likelihood: 3, default_impact: 3 });
    setModal('new');
  };
  const openEdit = (r) => {
    setForm({ risk_code: r.risk_code, risk_name: r.risk_name, risk_category: r.risk_category, description: r.description || '', default_likelihood: r.default_likelihood || 3, default_impact: r.default_impact || 3 });
    setModal(r);
  };

  const handleSave = async () => {
    if (!form.risk_code.trim() || !form.risk_name.trim()) { toast.error('Risk code and name are required'); return; }
    try {
      setSaving(true);
      if (modal === 'new') await vorApi.createRisk(form);
      else await vorApi.updateRisk(modal.id, form);
      toast.success(modal === 'new' ? 'Risk added' : 'Risk updated');
      setModal(null); load();
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete "${r.risk_name}"?`)) return;
    try { await vorApi.deleteRisk(r.id); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
        <button onClick={openNew} style={btnPrimary}>+ Add Risk</button>
      </div>
      <Table
        loading={loading}
        cols={['Code', 'Risk Name', 'Category', 'Default L', 'Default I', 'Score', 'Actions']}
        rows={items}
        emptyMsg="No risks defined."
        renderRow={r => (<>
          <Td style={{ color: '#9CA3AF', fontFamily: 'monospace' }}>{r.risk_code}</Td>
          <Td style={{ fontWeight: '500', color: '#111827' }}>{r.risk_name}</Td>
          <Td><span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', backgroundColor: '#F3EEFA', color: '#4A2B70', fontWeight: '600' }}>{r.risk_category}</span></Td>
          <Td>{r.default_likelihood}</Td>
          <Td>{r.default_impact}</Td>
          <Td style={{ fontWeight: '700', color: '#6B3FA0' }}>{(r.default_likelihood || 3) * (r.default_impact || 3)}</Td>
          <Td><div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => openEdit(r)} style={btnGhost}>Edit</button>
            <button onClick={() => handleDelete(r)} style={btnDanger}>Delete</button>
          </div></Td>
        </>)}
      />

      {modal && (
        <Modal title={modal === 'new' ? 'Add Risk' : 'Edit Risk'} onClose={() => setModal(null)}>
          <FormRow label="Risk Code (unique identifier)">
            <input value={form.risk_code} onChange={e => setForm(f => ({ ...f, risk_code: e.target.value }))}
              style={inputStyle} placeholder="e.g. TEC-001" />
          </FormRow>
          <FormRow label="Risk Name">
            <input value={form.risk_name} onChange={e => setForm(f => ({ ...f, risk_name: e.target.value }))}
              style={inputStyle} placeholder="e.g. Ransomware Attack" />
          </FormRow>
          <FormRow label="Category">
            <select value={form.risk_category} onChange={e => setForm(f => ({ ...f, risk_category: e.target.value }))} style={inputStyle}>
              {RISK_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </FormRow>
          <FormRow label="Description">
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
          </FormRow>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <FormRow label="Default Likelihood (1–5)">
              <input type="number" min="1" max="5" value={form.default_likelihood}
                onChange={e => setForm(f => ({ ...f, default_likelihood: Number(e.target.value) }))} style={inputStyle} />
            </FormRow>
            <FormRow label="Default Impact (1–5)">
              <input type="number" min="1" max="5" value={form.default_impact}
                onChange={e => setForm(f => ({ ...f, default_impact: Number(e.target.value) }))} style={inputStyle} />
            </FormRow>
          </div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button onClick={() => setModal(null)} style={btnGhost}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ── Tab 3: Controls Catalog ───────────────────────────────────────────────────

const CONTROL_CATEGORIES = [
  'Hardware and Physical Controls', 'Data Protection', 'Perimeter Defense',
  'Endpoint Protection', 'Email Security', 'Access Control',
  'Monitoring and Detection', 'Policies and Procedures',
];

const ControlsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ control_code: '', control_name: '', control_category: CONTROL_CATEGORIES[0], description: '', effectiveness_points: 10 });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { setLoading(true); const r = await vorApi.listControls(); setItems(r.data.controls || []); }
    catch { toast.error('Failed to load controls'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm({ control_code: '', control_name: '', control_category: CONTROL_CATEGORIES[0], description: '', effectiveness_points: 10 });
    setModal('new');
  };
  const openEdit = (c) => {
    setForm({ control_code: c.control_code, control_name: c.control_name, control_category: c.control_category, description: c.description || '', effectiveness_points: c.effectiveness_points || 10 });
    setModal(c);
  };

  const handleSave = async () => {
    if (!form.control_code.trim() || !form.control_name.trim()) { toast.error('Control code and name are required'); return; }
    try {
      setSaving(true);
      if (modal === 'new') await vorApi.createControl(form);
      else await vorApi.updateControl(modal.id, form);
      toast.success(modal === 'new' ? 'Control added' : 'Control updated');
      setModal(null); load();
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete "${c.control_name}"?`)) return;
    try { await vorApi.deleteControl(c.id); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
        <button onClick={openNew} style={btnPrimary}>+ Add Control</button>
      </div>
      <Table
        loading={loading}
        cols={['Code', 'Control Name', 'Category', 'Effectiveness', 'Actions']}
        rows={items}
        emptyMsg="No controls defined."
        renderRow={c => (<>
          <Td style={{ color: '#9CA3AF', fontFamily: 'monospace' }}>{c.control_code}</Td>
          <Td style={{ fontWeight: '500', color: '#111827' }}>{c.control_name}</Td>
          <Td><span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', backgroundColor: '#E0F2FE', color: '#0369A1', fontWeight: '600' }}>{c.control_category}</span></Td>
          <Td>{c.effectiveness_points} pts</Td>
          <Td><div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => openEdit(c)} style={btnGhost}>Edit</button>
            <button onClick={() => handleDelete(c)} style={btnDanger}>Delete</button>
          </div></Td>
        </>)}
      />

      {modal && (
        <Modal title={modal === 'new' ? 'Add Control' : 'Edit Control'} onClose={() => setModal(null)}>
          <FormRow label="Control Code (unique identifier)">
            <input value={form.control_code} onChange={e => setForm(f => ({ ...f, control_code: e.target.value }))}
              style={inputStyle} placeholder="e.g. AC-001" />
          </FormRow>
          <FormRow label="Control Name">
            <input value={form.control_name} onChange={e => setForm(f => ({ ...f, control_name: e.target.value }))}
              style={inputStyle} placeholder="e.g. Multi-Factor Authentication" />
          </FormRow>
          <FormRow label="Category">
            <select value={form.control_category} onChange={e => setForm(f => ({ ...f, control_category: e.target.value }))} style={inputStyle}>
              {CONTROL_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </FormRow>
          <FormRow label="Description">
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
          </FormRow>
          <FormRow label="Effectiveness Points">
            <input type="number" min="1" max="100" value={form.effectiveness_points}
              onChange={e => setForm(f => ({ ...f, effectiveness_points: Number(e.target.value) }))}
              style={{ ...inputStyle, width: '120px' }} />
          </FormRow>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button onClick={() => setModal(null)} style={btnGhost}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ── Tab 4: Control-Risk Mappings ──────────────────────────────────────────────

const MappingsTab = () => {
  const [items, setItems]     = useState([]);
  const [risks, setRisks]     = useState([]);
  const [controls, setControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null);
  const [form, setForm]       = useState({ risk_id: '', control_id: '', mitigation_percentage: 20 });
  const [saving, setSaving]   = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [mRes, rRes, cRes] = await Promise.all([vorApi.listMappings(), vorApi.listRisks(), vorApi.listControls()]);
      setItems(mRes.data.mappings || []);
      setRisks(rRes.data.risks || []);
      setControls(cRes.data.controls || []);
    } catch { toast.error('Failed to load mappings'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm({ risk_id: risks[0]?.id || '', control_id: controls[0]?.id || '', mitigation_percentage: 20 });
    setModal('new');
  };
  const openEdit = (m) => {
    setForm({ risk_id: m.risk_id, control_id: m.control_id, mitigation_percentage: m.mitigation_percentage });
    setModal(m);
  };

  const totalForControl = (controlId) =>
    items.filter(m => m.control_id === controlId).reduce((sum, m) => sum + Number(m.mitigation_percentage), 0);

  const handleSave = async () => {
    if (!form.risk_id || !form.control_id) { toast.error('Risk and Control are required'); return; }
    const total = totalForControl(Number(form.control_id));
    const existing = modal !== 'new' ? Number(modal.mitigation_percentage) : 0;
    if (total - existing + Number(form.mitigation_percentage) > 100) {
      if (!window.confirm('This mapping would bring total mitigation for this control over 100%. Continue anyway?')) return;
    }
    try {
      setSaving(true);
      if (modal === 'new') await vorApi.createMapping(form);
      else await vorApi.updateMapping(modal.id, form);
      toast.success(modal === 'new' ? 'Mapping added' : 'Mapping updated');
      setModal(null); load();
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (m) => {
    if (!window.confirm('Delete this mapping?')) return;
    try { await vorApi.deleteMapping(m.id); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const riskMap    = Object.fromEntries(risks.map(r => [r.id, r]));
  const controlMap = Object.fromEntries(controls.map(c => [c.id, c]));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <p style={{ margin: 0, color: '#6B7280', fontSize: '13px' }}>
          Maps which controls mitigate which risks, and by what percentage. Multiplicative formula is used during calculation.
        </p>
        <button onClick={openNew} style={btnPrimary}>+ Add Mapping</button>
      </div>
      <Table
        loading={loading}
        cols={['Risk', 'Control', 'Mitigation %', 'Control Total %', 'Actions']}
        rows={items}
        emptyMsg="No mappings defined."
        renderRow={m => {
          const ctrlTotal = totalForControl(m.control_id);
          return (<>
            <Td>{riskMap[m.risk_id]?.risk_name || `Risk #${m.risk_id}`}</Td>
            <Td>{controlMap[m.control_id]?.control_name || `Control #${m.control_id}`}</Td>
            <Td style={{ fontWeight: '600' }}>{m.mitigation_percentage}%</Td>
            <Td>
              <span style={{ color: ctrlTotal > 100 ? '#DC2626' : ctrlTotal > 80 ? '#D97706' : '#059669', fontWeight: '600' }}>
                {ctrlTotal}%
              </span>
              {ctrlTotal > 100 && <span style={{ marginLeft: '6px', fontSize: '11px', color: '#DC2626' }}>⚠ over 100%</span>}
            </Td>
            <Td><div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => openEdit(m)} style={btnGhost}>Edit</button>
              <button onClick={() => handleDelete(m)} style={btnDanger}>Delete</button>
            </div></Td>
          </>);
        }}
      />

      {modal && (
        <Modal title={modal === 'new' ? 'Add Control-Risk Mapping' : 'Edit Mapping'} onClose={() => setModal(null)}>
          <FormRow label="Risk">
            <select value={form.risk_id} onChange={e => setForm(f => ({ ...f, risk_id: e.target.value }))} style={inputStyle}>
              <option value="">Select risk…</option>
              {risks.map(r => <option key={r.id} value={r.id}>{r.risk_name} ({r.risk_category})</option>)}
            </select>
          </FormRow>
          <FormRow label="Control">
            <select value={form.control_id} onChange={e => setForm(f => ({ ...f, control_id: e.target.value }))} style={inputStyle}>
              <option value="">Select control…</option>
              {controls.map(c => <option key={c.id} value={c.id}>{c.control_name}</option>)}
            </select>
          </FormRow>
          {form.control_id && (
            <div style={{ marginBottom: '12px', padding: '8px 12px', backgroundColor: '#F9FAFB', borderRadius: '6px', fontSize: '13px', color: '#6B7280' }}>
              Current total mitigation for this control:{' '}
              <strong style={{ color: totalForControl(Number(form.control_id)) > 80 ? '#D97706' : '#374151' }}>
                {totalForControl(Number(form.control_id))}%
              </strong>
            </div>
          )}
          <FormRow label="Mitigation Percentage (%)">
            <input type="number" min="1" max="100" value={form.mitigation_percentage}
              onChange={e => setForm(f => ({ ...f, mitigation_percentage: Number(e.target.value) }))}
              style={{ ...inputStyle, width: '120px' }} />
            <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '3px' }}>
              How much this control reduces risk likelihood when implemented (multiplicative).
            </div>
          </FormRow>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button onClick={() => setModal(null)} style={btnGhost}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ── Main VorAdmin component ───────────────────────────────────────────────────

const TABS = [
  { key: 'questions', label: '🎯 Risk Tolerance Questions' },
  { key: 'risks',     label: '⚠️ Risk Catalog' },
  { key: 'controls',  label: '🛡️ Controls Catalog' },
  { key: 'mappings',  label: '🔗 Control-Risk Mappings' },
];

const VorAdmin = () => {
  const [tab, setTab] = useState('questions');

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: '0 0 4px', fontSize: '26px', fontWeight: '700', color: '#1a1a2e' }}>VoR Catalog Administration</h1>
        <p style={{ margin: 0, color: '#6B7280', fontSize: '14px' }}>
          Manage the risk and control catalogs used in Value of Risk assessments.
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '2px solid #E5D5F5' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '9px 18px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', border: 'none',
            borderBottom: tab === t.key ? '3px solid #6B3FA0' : '3px solid transparent',
            backgroundColor: 'transparent',
            color: tab === t.key ? '#6B3FA0' : '#6B7280',
            marginBottom: '-2px',
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div>
        {tab === 'questions' && <QuestionsTab />}
        {tab === 'risks'     && <RisksTab />}
        {tab === 'controls'  && <ControlsTab />}
        {tab === 'mappings'  && <MappingsTab />}
      </div>
    </div>
  );
};

export default VorAdmin;
